/**
 * @module app.core
 */

import { autoinject, bindingMode, observable } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { INotesConfig } from 'app/core/models/essette-interfaces';
import { NoteService } from 'app/core/services/note-service';
import { ApiQueryResponse } from 'app/shared/lib/essette-api';
import { apiCall } from 'app/shared/lib/api/api';
import { IApiSettings } from 'app/shared/lib/api/api-types';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { Toaster } from 'app/shared/lib/toaster';
import { getReference, getReferences } from 'app/shared/models/essette-types';
import { compareObjects } from 'app/shared/lib/compare-objects';
import { bindable } from 'aurelia-typed-observable-plugin';
import { NotesReloadEvent } from 'app/core/lib/events';
import { EventAggregator } from 'aurelia-event-aggregator';
import * as Core from 'app/core/models/essette-api';

@autoinject
export class SaveNote extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay, changeHandler: 'reload' })
    protected note: Core.Note = {};

    @bindable.object({ defaultBindingMode: bindingMode.oneTime })
    protected notesConfig: INotesConfig = {};

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    protected allowFlagSelect: boolean = true;

    protected settings: IApiSettings = {};

    // when false, Save button on NotesGrid component is disabled
    public noteChanged = false;

    protected originalNote: Core.Note = {};

    protected lockedCategoryId: string = '';

    @observable
    protected category: Core.NoteCategoryWithSubCategoryModel | null = null;
    protected subCategory: Core.NoteSubCategory | null = null;
    protected flags: Core.NoteFlag[] = [];

    @observable
    protected isCustom: boolean = false;

    @observable
    protected definition: Core.NoteDefinition | null = null;

    @observable
    protected text: string = '';
    protected customText: string = '';

    protected categories: Core.NoteCategory[] = [];

    protected maxTextLength: number = 4000;


    ////****************** Element References ***************************************////



    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly toaster: Toaster,
        private readonly service: NoteService,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.service.configure(this.notesConfig);
        this.loadSettings();

        if(this.notesConfig.categoryId) {
            this.lockedCategoryId = this.notesConfig.categoryId;

            if(this.category) {
                this.category.subCategories = [];
                this.category.flags = [];
            }
        }

        this.loadCategories();
        this.reload();
    }


    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        // triggered when note changes so that originalNote is rebuilt
        this.category = this.note.id ? this.note.category! : null;
        this.subCategory = this.note.id ? (this.note.category!.subCategory ? this.note.category?.subCategory! : null) : null;
        this.flags = this.note.id ? [...this.note.flags!] : [];
        this.text = this.note.id ? (this.note.text || '') : '';
        this.noteChanged = false;
        this.originalNote = this.buildNote();
    }

    protected textChanged(): void {
        this.noteChanged = this.hasChanges();
    }


    ////****************** Functions ************************************************////
    public hasChanges(): boolean {
        const note = this.buildNote();

        const result = compareObjects(this.originalNote, note, { returnDifferences: true });
        return !result.areEqual;
    }

    public reset(): void {
        this.category = this.originalNote.category || null;
        this.subCategory = this.originalNote.category && this.originalNote.category.subCategory || null;
        this.flags = [];
        if(this.originalNote.flags && this.originalNote.flags.length > 0) {
            this.flags = [...this.originalNote.flags];
        }
        this.isCustom = false;
        this.definition = this.originalNote.definition || null;
        this.text = this.originalNote.text || '';
        this.customText = this.originalNote.text || '';
    }

    public async submit(): Promise<boolean> {
        try {
            const note = this.buildNote();

            if(!(this.notesConfig && this.notesConfig.addTargetProperty)) {
                throw new Error("Notes configuration must define addTargetProperty.")
            }

            // If Note is an insert, set target property. If an update, the core note api update will be called and not require a target property
            if(!note.id) {
                this.notesConfig.addTargetProperty(note);
            }

            await this.saveNote(note);

            return true;
        }
        catch(error) {
            this.logger.error('Error Saving Note', error);
            return false;
        }
    }

    protected categoryChanged(): void {
        if(this.category && this.note.id && this.note.flags) {
            this.flags = [...this.note.flags];
        } else if(this.category && this.category.flags) {
            this.flags = [...this.category.flags];
        } else {
            this.flags = [];
        }

        if(this.category && this.note.id && this.note.category?.subCategory) {
            this.subCategory = this.note.category.subCategory;
        } else if(this.category && this.category.subCategories && this.category.subCategories.length === 1) {
            this.subCategory = this.category.subCategories[0];
        } else {
            this.subCategory = null;
        }
        this.definition = null;

        this.noteChanged = this.hasChanges();
    }

    protected isCustomChanged(): void {
        if(!this.isCustom) {
            if(this.definition?.id) {
                // switching to standard notes with a valid definition selected, stash custom text
                this.customText = this.text;
            } else {
                this.text = '';
                this.customText = '';
            }
        }
    }

    protected definitionChanged(): void {
        if(this.definition?.id && this.definition.text) {
            // use definition's text in case the user switches back to custom (using it as a template)
            this.text = this.definition.text;
        }
        else {
            // when no definition is selected, switch text back to what it was before for convenience
            this.text = this.customText;
        }

        this.noteChanged = this.hasChanges();
    }

    protected buildNote(): Core.Note {
        const note: Core.Note = {
            id: this.note.id || null,
            category: getReference(this.category, { subCategory: getReference(this.subCategory) || undefined }),
            flags: getReferences(this.flags),
            definition: getReference(!this.isCustom && this.definition || undefined) || undefined,
            text: (this.isCustom || this.note.id) && this.text || undefined,
        };

        if(this.notesConfig.taskId) {
            note.metadata = {
                created: {
                    byTask: { id: this.notesConfig.taskId }
                }
            };
        }

        return note;
    }

    protected hasDefinitions(category: Core.NoteCategory): boolean {
        const hasDefinitions = (category?.definitions?.length || 0) > 0;

        this.isCustom = !hasDefinitions || !!this.note.id;
        return hasDefinitions;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadSettings(): Promise<void> {
        try {
            this.state.beginLoad();

            const response = await this.service.loadSettings();
            this.settings = response.settings;

            const maxLengthSetting = getApiSetting(this.settings, 'text.$maximumLength');
            if(maxLengthSetting) this.maxTextLength = maxLengthSetting as number;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveNote(note: Core.Note): Promise<void> {
        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.saveNote(note);
            isSuccessful = response.isSuccessful;
            this.events.publish(new NotesReloadEvent());
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.saved('core:note.$', isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async loadCategories(): Promise<void> {
        const includes = [
            'definitions',
            'flags',
            'subCategories'
        ];

        const fields = [
            'definitions.description',
            'definitions.text',
            'description',
            'flags.description',
            'subCategories.description'
        ];

        try {
            this.state.beginLoad();
            let response: ApiQueryResponse<Core.NoteCategory>;
            if(this.lockedCategoryId) response = await this.service.loadCategoryById(this.lockedCategoryId, includes, fields);
            else response = await this.service.loadCategories({ all: true }, includes, this.notesConfig.excludeSystem, undefined, undefined, fields);
            this.categories = response.results;

            // auto-select if only one category
            if(this.categories.length === 1) this.category = this.categories[0];

            // auto-select if category has only one sub-category
            if(this.category?.subCategories?.length === 1) this.subCategory = this.category.subCategories[0];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

}
